import { useState } from 'react';
import { useLang } from 'hooks/lang';

import { loaders, validateProjectName, validateProjectNameLooksLikeEmailSubject } from 'entities';

import {
  Row,
  Checkbox,
  LabeledInput,
  LabeledSelect,
  Label,
  Button,
  Column,
  RadioInput,
} from 'components/Blocks';
import { EntitySelect } from 'components/Entities';
import { getUserLabel } from 'components/Inputs';
import {
  CalendarRangeIcon,
  ChevronRightIcon,
  LocationPinMapIcon,
  TargetIcon,
  TicketIcon,
  UserBookIcon,
  UserSquareIcon,
} from 'components/Icons';

import { ProjectNameAdvisory } from 'pages/Projects/components';
import { ProjectTypeRenderer } from './ProjectType';

import { useConfirmation } from 'providers/Confirmation';

import { StepComponentProps } from '../types';
import { validateProjectInfoFilling } from '../utils';
import { CLIENT_ORIGIN_OPTIONS, DEVICE_OPTIONS } from 'utils/constants';
import { sortByName } from 'utils/array';

export function ProjectInfo({
  state,
  dispatch,
  handleChange,
  setStep,
}: StepComponentProps): JSX.Element {
  const [invalidNameMessage, setInvalidNameMessage] = useState('');

  const { t } = useLang();

  const clients = loaders.clients.list.use({
    defaultValue: [],
    parser: sortByName,
  });

  const hasNotFilled = validateProjectInfoFilling(state);

  const handleNameChange = (projectName: string): void => {
    const regex = /_/g;
    const name = projectName.replace(regex, ' ');

    const multiWhiteSpaces = /(\s+)/g;

    dispatch({ name: name.replace(multiWhiteSpaces, ' ') });
  };

  const { requestConfirmation } = useConfirmation();

  const handleNextClick = (): void => {
    if (hasNotFilled) return;

    const nameError = validateProjectName({ clients: clients.data, project: state });

    if (nameError) return setInvalidNameMessage(t(nameError) as string);

    setInvalidNameMessage('');

    const requireConfirmation = validateProjectNameLooksLikeEmailSubject(state.name);

    if (!requireConfirmation) return setStep(2);

    requestConfirmation({
      title: t('confirm-project-name')!,

      onConfirm: async () => setStep(2),

      size: 'medium',

      text: <ProjectNameAdvisory name={state.name} />,
    });
  };

  return (
    <div className="grid w-full grid-cols-2 gap-6 phone:grid-cols-1">
      <LabeledInput
        id="name"
        value={state.name}
        label={t('project-name')}
        onChange={({ target }) => handleNameChange(target.value)}
        error={invalidNameMessage || null}
        required
        containerClassName="col-span-2 phone:col-span-1"
      />

      <ProjectTypeRenderer projectType={state.typeConfig} dispatch={dispatch} />

      <LabeledSelect
        id="client"
        options={clients.data}
        value={state.client}
        onChange={handleChange}
        labelIcon={UserSquareIcon}
        label={t('client') || ''}
        loading={clients.errorOrLoading}
        isSearchable
        labelKey="name"
      />

      <LabeledSelect
        id="clientOrigin"
        value={state.clientOrigin}
        labelIcon={LocationPinMapIcon}
        onChange={handleChange}
        label={t('client-origin') || ''}
        options={sortByName(
          CLIENT_ORIGIN_OPTIONS.map(({ value, getLabel }) => ({
            value,
            name: getLabel(t),
            label: getLabel(t),
          })),
        )}
      />

      <LabeledInput
        id="ticket"
        value={state.ticket}
        label={t('freshdesk-ticket-number')}
        onChange={handleChange}
        labelIcon={TicketIcon}
        type="number"
        required
      />

      <LabeledInput
        id="estimatedCompletionWorkingDays"
        value={state.estimatedCompletionWorkingDays}
        label={t('estimated-completion-in-working-days')}
        type="number"
        labelIcon={CalendarRangeIcon}
        onChange={(event) => /^\d*$/.test(event.target.value) && handleChange(event)}
        required
      />

      <Column gap="2" className="col-span-2 phone:col-span-1">
        <Label icon={TargetIcon}>{t('device-target')}</Label>

        <RadioInput
          orientation="horizontal"
          className="w-full shrink rounded-md border py-2 text-sm transition-colors hover:bg-hover"
          selectedClassName="border-primary text-primary"
          alignItems="stretch"
          id="deviceTarget"
          value={state.deviceTarget}
          onChange={handleChange}
          options={DEVICE_OPTIONS.map(({ getLabel, id, icon }) => ({
            value: id,
            label: getLabel(t),
            Icon: icon,
          }))}
          render={({ Icon, label }) => (
            <Column gap="2" alignItems="center">
              <Icon size={24} />
              <span>{label}</span>
            </Column>
          )}
        />
      </Column>

      <Column gap="2" className="col-span-2 phone:col-span-1">
        <Row justifyContent="space-between">
          <Label icon={UserBookIcon}>{t('sales-person')}</Label>

          <Row gap="2">
            <Checkbox
              size="small"
              id="another-source"
              checked={state.salesPerson === 'ANOTHER_SOURCE'}
              onChange={({ target }) =>
                dispatch({
                  salesPerson: target.checked ? 'ANOTHER_SOURCE' : '',
                })
              }
            />

            <Label className="text-sm" htmlFor="another-source">
              {t('another-source')}
            </Label>
          </Row>
        </Row>

        <EntitySelect
          id="salesPerson"
          target="users.salesUsers"
          value={state.salesPerson}
          onChange={handleChange}
          disabled={state.salesPerson === 'ANOTHER_SOURCE'}
          placeholder={state.salesPerson === 'ANOTHER_SOURCE' ? t('another-source') : 'Select...'}
          formatLabel={getUserLabel}
          fullWidth
          labelKey="name"
        />
      </Column>

      <Button
        type="button"
        onClick={handleNextClick}
        disabled={hasNotFilled}
        className="col-start-2 justify-self-end phone:col-start-1 phone:max-w-full"
      >
        {t('next')}
        <ChevronRightIcon />
      </Button>
    </div>
  );
}
